<script setup lang="ts">
import { computed, toRef } from 'vue';
import { ApplicationSection } from '@/shared/application/ApplicationSection';
import ProgressBarElement from '@/application/shared/infrastructure/components/ProgressBarElement.vue';

const props = defineProps<{
  currentSection: number,
  sectionList: Array<ApplicationSection>,
  showLastStepInFlow?: boolean
}>();

interface EmitInterface {
  (event: 'goToSection', value: number, isLast: boolean): void
}

const emits = defineEmits<EmitInterface>();

const filteredSections = toRef(() => props.sectionList.filter(section => !section.hideInProgressBar));

const offSet = props.showLastStepInFlow ? 0 : 1;
const currentStep = computed(() => props.currentSection - hiddenBeforeCurrent.value);
const lowerVisibleSectionIndex = computed(() => {
  for (let i = props.currentSection - 1; i >= 0; i--) {
    if (!props.sectionList[i].hideInProgressBar) {
      return i + 1;
    }
  }

  return 0;
});
const higherVisibleSectionIndex = computed(() => props.sectionList.findIndex((section, index) => !section.hideInProgressBar && index >= lowerVisibleSectionIndex.value));
const offset = computed(() => {
  return (100 / filteredSections.value.length / (higherVisibleSectionIndex.value - lowerVisibleSectionIndex.value + 1) * (higherVisibleSectionIndex.value - props.currentSection));
});

const elementToHighlight = computed(() => {
  if (higherVisibleSectionIndex.value === props.currentSection) {
    return currentStep.value;
  }

  return currentStep.value - (higherVisibleSectionIndex.value - lowerVisibleSectionIndex.value);
});
const maxElementToHighlight = computed((previous?: number): number => Math.max(elementToHighlight.value, previous ?? 0));
const hiddenBeforeCurrent = computed(() => props.sectionList.slice(0, props.currentSection).filter(section => section.hideInProgressBar).length);
const sectionsNumbers = computed(() => Array.from(Array(filteredSections.value.length - offSet).keys()).map(i => i + 1));

const percent = computed(() => {
  const totalVisibleSections = filteredSections.value.length - 1 - offSet;

  return Number(((currentStep.value / totalVisibleSections * 100) - offset.value).toFixed(0));
});

const goToSection = (i: number, isLast: boolean): void => {
  const shortTitle = filteredSections.value[i].shortTitle;
  const index = props.sectionList.findIndex(section => section.shortTitle === shortTitle);
  emits('goToSection', index, isLast);
};

</script>
<template>
  <div class="flex items-center justify-between relative z-0 mb-4 mx-12 lg:mt-8">
    <span class="absolute w-full h-1 flex">
      <span class="h-1 bg-blue-400 transition-width duration-500" :style="`width: ${percent}%`"></span>
      <span class="h-1 bg-gray-200 flex-grow transition-width duration-300"></span>
    </span>
    <ProgressBarElement v-for="i in sectionsNumbers" :key="i" :max-section="sectionsNumbers[maxElementToHighlight]" :current-section="sectionsNumbers[elementToHighlight]" :number="i" :title="filteredSections[i - 1].shortTitle" :icon="filteredSections[i - 1].icon" @go-to-section="goToSection" />
  </div>
</template>
